import { ErrorHandler, inject, OnDestroy, Service } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { interval, Subscription } from 'rxjs';

import { ADVANIA_VERSIONING_CONFIG } from './advania-versioning.config';

@Service()
export class VersionService implements OnDestroy {
    private readonly _config = inject(ADVANIA_VERSIONING_CONFIG);
    private readonly _errorHandler = inject(ErrorHandler);
    private readonly _swUpdate = inject(SwUpdate);

    #subscriptions: Subscription[] = [];

    constructor() {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        const { checkOnInit, enableDebugLogging, onUpdateRequired, onUnrecoverable, pollIntervalInMs } = this._config;

        if (enableDebugLogging) {
            console.log('Advania Versioning: Service Worker is enabled. Version checking is active.');
        }

        this.#subscriptions.push(
            this._swUpdate.versionUpdates.subscribe((event) => {
                if (event.type === 'VERSION_READY') {
                    onUpdateRequired();
                }
            }),
        );

        this.#subscriptions.push(
            this._swUpdate.unrecoverable.subscribe(() => {
                onUnrecoverable();
            }),
        );

        if (checkOnInit) {
            void this.checkNow();
        }

        if (pollIntervalInMs !== null && pollIntervalInMs > 0) {
            this.#subscriptions.push(
                interval(pollIntervalInMs).subscribe(() => {
                    void this.checkNow();
                }),
            );
        }
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    async checkNow(): Promise<void> {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        await this.runAction(() => this._swUpdate.checkForUpdate());
    }

    async acceptUpdate(): Promise<boolean> {
        if (!this._swUpdate.isEnabled) {
            return false;
        }

        const didActivateUpdate = await this.runAction(() => this._swUpdate.activateUpdate());

        if (!didActivateUpdate) {
            return false;
        }

        this._config.onUpdateNow();

        return true;
    }

    private async runAction(action: () => Promise<unknown>): Promise<boolean> {
        try {
            await action();

            return true;
        } catch (error: unknown) {
            this._errorHandler.handleError(error);

            return false;
        }
    }
}
